import React from 'react';
import { Link } from 'react-router-dom';
import { SiteLayout } from '../components/site/SiteLayout';
import { ArrowLeft, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <SiteLayout testid="privacy-page">

      {/* Content */}
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-16 md:py-20">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Link>

        <div className="glass-panel rounded-3xl p-8 md:p-12 mb-10">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-bold text-white font-display">Privacy Policy</h1>
              <p className="text-slate-400 mt-2">Last Updated: January 2026</p>
            </div>
          </div>
        </div>

        <div className="max-w-none">
          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Introduction</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              At Zivae Financial, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Information We Collect</h2>
            <h3 className="text-xl font-semibold text-white font-display mb-3">Personal Information</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>Schedule a consultation or appointment</li>
              <li>Contact us through our website forms</li>
              <li>Sign up for retirement seminars or webinars</li>
              <li>Request information about our services</li>
              <li>Subscribe to our newsletter or communications</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              This information may include your name, email address, phone number, date of birth, address, and financial information relevant to retirement planning.
            </p>

            <h3 className="text-xl font-semibold text-white font-display mb-3 mt-6">Automatically Collected Information</h3>
            <p className="text-slate-300 leading-relaxed mb-4">
              When you visit our website, we may automatically collect certain information about your device, including:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>IP address and browser type</li>
              <li>Operating system and device information</li>
              <li>Pages visited and time spent on our site</li>
              <li>Referring website addresses</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">How We Use Your Information</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>Provide retirement consulting and financial planning services</li>
              <li>Respond to your inquiries and communicate with you</li>
              <li>Schedule and manage appointments and consultations</li>
              <li>Send you relevant information about retirement planning and our services</li>
              <li>Comply with legal and regulatory requirements</li>
              <li>Improve our website and services</li>
              <li>Detect and prevent fraud or unauthorized activities</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Information Sharing and Disclosure</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our business and providing services to you</li>
              <li><strong>Legal Requirements:</strong> When required by law, court order, or government regulation</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              <li><strong>Protection of Rights:</strong> To protect the rights, property, or safety of Zivae Financial, our clients, or others</li>
            </ul>
            
            <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-orange-500 mb-4">
              <h3 className="text-xl font-semibold text-white font-display mb-3">Mobile Information Privacy</h3>
              <p className="text-slate-300 leading-relaxed">
                <strong>No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.</strong> Information sharing to subcontractors in support services, such as customer service, is permitted.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Data Security</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We implement appropriate technical and organizational security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Your Privacy Rights</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>Access to your personal information</li>
              <li>Correction of inaccurate information</li>
              <li>Deletion of your personal information</li>
              <li>Restriction of processing</li>
              <li>Data portability</li>
              <li>Objection to processing</li>
              <li>Withdrawal of consent</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-4">
              To exercise these rights, please contact us using the information provided below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie preferences through your browser settings, but disabling cookies may affect certain features of our website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Third-Party Links</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Children's Privacy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Changes to This Privacy Policy</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website with a new "Last Updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">SMS/Text Messaging Policy</h2>
            
            <p className="text-slate-300 leading-relaxed mb-4">
              By providing your phone number on our website, forms, or during scheduling, you consent to receive SMS/text messages from Zivae Financial for informational and service-related purposes.
            </p>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Types of Messages You May Receive:</h3>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-6">
              <li>Appointment confirmations and reminders</li>
              <li>Follow-up messages regarding your Medicare or financial consultation</li>
              <li>Educational updates related to Medicare, retirement planning, and financial services</li>
              <li>Important notifications related to your inquiry or services requested</li>
            </ul>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Message Frequency:</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              Message frequency will vary based on your interaction with our services but will generally not exceed 2–5 messages per week.
            </p>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Opt-In Process:</h3>
            <p className="text-slate-300 leading-relaxed mb-4">Users opt in by:</p>
            <ul className="list-disc pl-6 text-slate-300 space-y-2 mb-4">
              <li>Submitting their phone number through our website forms</li>
              <li>Scheduling an appointment via our booking system</li>
              <li>Requesting information about our services</li>
            </ul>
            <p className="text-slate-300 leading-relaxed mb-6">
              By submitting your information, you are providing express consent to receive text messages from Zivae Financial.
            </p>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Opt-Out Instructions:</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              You can opt out of receiving SMS messages at any time by replying <strong>STOP</strong> to any message. Once you opt out, you will no longer receive text messages unless you re-subscribe.
            </p>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Help Instructions:</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              For assistance, reply <strong>HELP</strong> or contact us directly at <a href="tel:2272381080" className="text-orange-400 hover:text-orange-300 font-semibold">(227) 238-1080</a>.
            </p>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Message & Data Rates:</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              Message and data rates may apply depending on your mobile carrier.
            </p>

            <h3 className="text-2xl font-semibold text-white font-display mb-3">Privacy Commitment:</h3>
            <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-orange-500">
              <p className="text-slate-300 leading-relaxed">
                <strong>Your information will never be sold or shared with third parties for marketing purposes.</strong> Your data is used solely to provide and improve our services.
              </p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-3xl font-bold text-white font-display mb-4">Contact Us</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="glass-panel rounded-2xl p-6 border-l-4 border-l-orange-500">
              <p className="text-white font-display font-semibold mb-2">Zivae Financial</p>
              <p className="text-slate-300">Virtual Nationwide</p>
              <p className="text-slate-300">Phone: <a href="tel:2272381080" className="text-orange-400 hover:text-orange-300">(227) 238-1080</a></p>
              <p className="text-slate-300 mt-2">Licensed in 19 states: CA, FL, GA, IL, IN, KY, MD, ME, MI, NC, NM, NY, OH, OR, PA, SC, TX, VA, WI</p>
            </div>
          </section>

          <section className="bg-orange-500/10 rounded-2xl p-6 border border-orange-400/30">
            <h3 className="text-xl font-semibold text-white font-display mb-3">Consent</h3>
            <p className="text-slate-300 leading-relaxed">
              By using our website and services, you consent to the collection and use of your information as described in this Privacy Policy.
            </p>
          </section>
        </div>
      </div>
    </SiteLayout>
  );
};

export default PrivacyPolicy;